/**
 * AI Analytics Dashboard
 * GPT-5.2 powered sales forecasting, inventory predictions, and business insights
 */
import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Progress } from '@/components/ui/progress';
import { 
  Brain, TrendingUp, TrendingDown, Sparkles, Target, Users, 
  Package, Calendar, BarChart3, Lightbulb, AlertTriangle, 
  CheckCircle, Loader2, RefreshCcw, ArrowUpRight, ArrowDownRight,
  Clock, DollarSign, UserCheck, Utensils, Cpu
} from 'lucide-react';
import { useAuth } from '@/context/AuthContext';
import api from '@/services/api';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';
import {
  LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid,
  Tooltip, ResponsiveContainer, Legend
} from 'recharts';

const formatCurrency = (value) => {
  return new Intl.NumberFormat('nb-NO', {
    style: 'currency',
    currency: 'NOK',
    minimumFractionDigits: 0,
  }).format(value || 0);
};

export default function AIAnalyticsDashboard() {
  const { currentOutlet, outletId } = useAuth();
  const [loading, setLoading] = useState({
    forecast: false,
    inventory: false,
    staffing: false,
    menu: false,
    insights: false,
  });
  const [activeTab, setActiveTab] = useState('forecast');
  
  // AI Data states
  const [forecast, setForecast] = useState(null);
  const [inventoryRecs, setInventoryRecs] = useState(null);
  const [staffingRecs, setStaffingRecs] = useState(null);
  const [menuRecs, setMenuRecs] = useState(null);
  const [businessInsights, setBusinessInsights] = useState(null);

  // Fetch Sales Forecast
  const fetchForecast = useCallback(async () => {
    if (!outletId) return;
    
    setLoading(prev => ({ ...prev, forecast: true }));
    try {
      const response = await api.get(`/ai/llm/forecast/${outletId}`, {
        params: { days_ahead: 7 }
      });
      setForecast(response.data);
    } catch (err) {
      console.error('Failed to fetch forecast:', err);
      toast.error('Failed to load AI forecast');
    } finally {
      setLoading(prev => ({ ...prev, forecast: false }));
    }
  }, [outletId]);

  // Fetch Inventory Recommendations
  const fetchInventoryRecs = useCallback(async () => {
    if (!outletId) return;
    
    setLoading(prev => ({ ...prev, inventory: true }));
    try {
      const response = await api.get(`/ai/llm/inventory/${outletId}`);
      setInventoryRecs(response.data);
    } catch (err) {
      console.error('Failed to fetch inventory recs:', err);
    } finally {
      setLoading(prev => ({ ...prev, inventory: false }));
    }
  }, [outletId]);

  // Fetch Staffing Recommendations
  const fetchStaffingRecs = useCallback(async () => {
    if (!outletId) return;
    
    setLoading(prev => ({ ...prev, staffing: true }));
    try {
      const response = await api.get(`/ai/llm/staffing/${outletId}`);
      setStaffingRecs(response.data);
    } catch (err) {
      console.error('Failed to fetch staffing recs:', err);
    } finally {
      setLoading(prev => ({ ...prev, staffing: false }));
    }
  }, [outletId]);

  // Fetch Menu Recommendations
  const fetchMenuRecs = useCallback(async () => {
    if (!outletId) return;
    
    setLoading(prev => ({ ...prev, menu: true }));
    try {
      const response = await api.get(`/ai/llm/menu/${outletId}`);
      setMenuRecs(response.data);
    } catch (err) {
      console.error('Failed to fetch menu recs:', err);
    } finally {
      setLoading(prev => ({ ...prev, menu: false }));
    }
  }, [outletId]);

  // Fetch Business Insights
  const fetchBusinessInsights = useCallback(async () => {
    setLoading(prev => ({ ...prev, insights: true }));
    try {
      const response = await api.get('/ai/llm/insights', {
        params: { period_days: 30 }
      });
      setBusinessInsights(response.data);
    } catch (err) {
      console.error('Failed to fetch insights:', err);
    } finally {
      setLoading(prev => ({ ...prev, insights: false }));
    }
  }, []);

  useEffect(() => {
    if (outletId) {
      fetchForecast();
    }
  }, [outletId, fetchForecast]);

  // Refresh current tab data
  const refreshCurrentTab = () => {
    switch (activeTab) {
      case 'forecast': fetchForecast(); break;
      case 'inventory': fetchInventoryRecs(); break;
      case 'staffing': fetchStaffingRecs(); break;
      case 'menu': fetchMenuRecs(); break;
      case 'insights': fetchBusinessInsights(); break;
    }
  };

  // Load data when tab changes
  useEffect(() => {
    if (activeTab === 'inventory' && !inventoryRecs) fetchInventoryRecs();
    if (activeTab === 'staffing' && !staffingRecs) fetchStaffingRecs();
    if (activeTab === 'menu' && !menuRecs) fetchMenuRecs();
    if (activeTab === 'insights' && !businessInsights) fetchBusinessInsights();
  }, [activeTab, inventoryRecs, staffingRecs, menuRecs, businessInsights]);

  const getTrendIcon = (trend) => {
    if (trend === 'up') return <ArrowUpRight className="w-4 h-4 text-green-600" />;
    if (trend === 'down') return <ArrowDownRight className="w-4 h-4 text-red-600" />;
    return <span className="w-4 h-4" />;
  };

  const getConfidenceBadge = (confidence) => {
    if (confidence === 'high') return <Badge className="bg-green-100 text-green-700">High Confidence</Badge>;
    if (confidence === 'medium') return <Badge className="bg-amber-100 text-amber-700">Medium</Badge>;
    return <Badge className="bg-gray-100 text-gray-700">Low</Badge>;
  };

  // Prepare chart data from forecast
  const forecastChartData = forecast?.daily_forecasts?.map(f => ({
    date: new Date(f.date).toLocaleDateString('en-US', { weekday: 'short', day: 'numeric' }),
    predicted: f.predicted_sales,
    confidence: f.confidence === 'high' ? 100 : f.confidence === 'medium' ? 70 : 40,
  })) || [];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <div className="flex items-center gap-2">
            <Brain className="w-8 h-8 text-purple-600" />
            <h1 className="font-display text-3xl text-gray-900">AI Analytics</h1>
          </div>
          <p className="text-gray-500 text-sm mt-1">
            {currentOutlet?.name || 'All Outlets'} - AI-powered insights and predictions
          </p>
        </div>
        <Button variant="outline" onClick={refreshCurrentTab}>
          <RefreshCcw className="w-4 h-4 mr-2" />
          Refresh Analysis
        </Button>
      </div>

      {/* Main Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid w-full grid-cols-5">
          <TabsTrigger value="forecast" data-testid="tab-forecast">
            <TrendingUp className="w-4 h-4 mr-2" />
            Sales Forecast
          </TabsTrigger>
          <TabsTrigger value="inventory" data-testid="tab-inventory">
            <Package className="w-4 h-4 mr-2" />
            Inventory AI
          </TabsTrigger>
          <TabsTrigger value="staffing" data-testid="tab-staffing">
            <UserCheck className="w-4 h-4 mr-2" />
            Staffing AI
          </TabsTrigger>
          <TabsTrigger value="menu" data-testid="tab-menu">
            <Utensils className="w-4 h-4 mr-2" />
            Menu AI
          </TabsTrigger>
          <TabsTrigger value="insights" data-testid="tab-insights">
            <Lightbulb className="w-4 h-4 mr-2" />
            Business Insights
          </TabsTrigger>
        </TabsList>

        {/* Sales Forecast Tab */}
        <TabsContent value="forecast" className="space-y-6 mt-6">
          {loading.forecast ? (
            <Card>
              <CardContent className="flex items-center justify-center py-20">
                <div className="text-center">
                  <Loader2 className="w-10 h-10 animate-spin text-purple-600 mx-auto mb-4" />
                  <p className="text-gray-600">Analyzing sales patterns with GPT-5.2...</p>
                </div>
              </CardContent>
            </Card>
          ) : forecast?.error ? (
            <Card className="border-amber-200 bg-amber-50">
              <CardContent className="py-8 text-center">
                <AlertTriangle className="w-12 h-12 text-amber-500 mx-auto mb-3" />
                <p className="text-amber-700">{forecast.error}</p>
                <Button variant="outline" className="mt-4" onClick={fetchForecast}>
                  Try Again
                </Button>
              </CardContent>
            </Card>
          ) : forecast?.success ? (
            <>
              {/* Weekly Summary */}
              {forecast.weekly_summary && (
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                  <Card>
                    <CardContent className="p-4">
                      <div className="flex items-center gap-3">
                        <div className="p-2 bg-purple-100 rounded-lg">
                          <DollarSign className="w-5 h-5 text-purple-600" />
                        </div>
                        <div>
                          <p className="text-2xl font-bold">
                            {formatCurrency(forecast.weekly_summary.total_predicted)}
                          </p>
                          <p className="text-sm text-gray-500">Predicted Weekly Sales</p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                  <Card>
                    <CardContent className="p-4">
                      <div className="flex items-center gap-3">
                        <div className={cn(
                          "p-2 rounded-lg",
                          forecast.weekly_summary.trend === 'up' ? 'bg-green-100' :
                          forecast.weekly_summary.trend === 'down' ? 'bg-red-100' : 'bg-gray-100'
                        )}>
                          {forecast.weekly_summary.trend === 'up' ? (
                            <TrendingUp className="w-5 h-5 text-green-600" />
                          ) : forecast.weekly_summary.trend === 'down' ? (
                            <TrendingDown className="w-5 h-5 text-red-600" />
                          ) : (
                            <BarChart3 className="w-5 h-5 text-gray-600" />
                          )}
                        </div>
                        <div>
                          <p className="text-2xl font-bold capitalize">{forecast.weekly_summary.trend}</p>
                          <p className="text-sm text-gray-500">Trend Direction</p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                  <Card className="md:col-span-2">
                    <CardContent className="p-4">
                      <p className="text-sm text-gray-500 mb-2">Key Insights</p>
                      <div className="flex flex-wrap gap-2">
                        {forecast.weekly_summary.key_insights?.map((insight, idx) => (
                          <Badge key={idx} variant="outline" className="text-xs">
                            {insight}
                          </Badge>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                </div>
              )}

              {/* Forecast Chart */}
              <Card>
                <CardHeader>
                  <CardTitle>7-Day Sales Forecast</CardTitle>
                  <CardDescription>AI-predicted daily sales based on historical patterns</CardDescription>
                </CardHeader>
                <CardContent>
                  {forecastChartData.length > 0 ? (
                    <ResponsiveContainer width="100%" height={300}>
                      <BarChart data={forecastChartData}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="date" />
                        <YAxis tickFormatter={(v) => `${(v/1000).toFixed(0)}k`} />
                        <Tooltip formatter={(value) => formatCurrency(value)} />
                        <Bar dataKey="predicted" fill="#9333ea" radius={[4, 4, 0, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  ) : (
                    <div className="h-[300px] flex items-center justify-center text-gray-500">
                      No forecast data available
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Daily Forecasts Detail */}
              <Card>
                <CardHeader>
                  <CardTitle>Daily Breakdown</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {forecast.daily_forecasts?.map((day, idx) => (
                      <div key={idx} className="flex items-center gap-4 p-3 rounded-lg border hover:bg-gray-50">
                        <div className="w-24 font-medium">
                          {new Date(day.date).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })}
                        </div>
                        <div className="flex-1">
                          <Progress 
                            value={(day.predicted_sales / (forecast.weekly_summary?.total_predicted || 1)) * 700} 
                            className="h-2"
                          />
                        </div>
                        <div className="w-32 text-right font-mono font-bold">
                          {formatCurrency(day.predicted_sales)}
                        </div>
                        <div className="w-28">
                          {getConfidenceBadge(day.confidence)}
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>

              {/* Recommendations */}
              {forecast.recommendations?.length > 0 && (
                <Card className="border-purple-200 bg-purple-50/50">
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <Lightbulb className="w-5 h-5 text-purple-600" />
                      AI Recommendations
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-2">
                      {forecast.recommendations.map((rec, idx) => (
                        <div key={idx} className="flex items-start gap-3 p-3 bg-white rounded-lg">
                          <Sparkles className="w-5 h-5 text-purple-500 mt-0.5" />
                          <p className="text-gray-700">{rec}</p>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}
            </>
          ) : (
            <Card>
              <CardContent className="py-12 text-center">
                <Brain className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                <p className="text-gray-500">Click refresh to generate AI forecast</p>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        {/* Inventory AI Tab */}
        <TabsContent value="inventory" className="space-y-6 mt-6">
          {loading.inventory ? (
            <Card>
              <CardContent className="flex items-center justify-center py-20">
                <div className="text-center">
                  <Loader2 className="w-10 h-10 animate-spin text-purple-600 mx-auto mb-4" />
                  <p className="text-gray-600">Analyzing inventory levels...</p>
                </div>
              </CardContent>
            </Card>
          ) : inventoryRecs?.success ? (
            <>
              {/* Urgent Actions */}
              {inventoryRecs.urgent_actions?.length > 0 && (
                <Card className="border-red-200">
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2 text-red-700">
                      <AlertTriangle className="w-5 h-5" />
                      Urgent Actions Required
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-3">
                      {inventoryRecs.urgent_actions.map((action, idx) => (
                        <div key={idx} className="flex items-center gap-4 p-3 rounded-lg bg-red-50 border border-red-200">
                          <Badge className={cn(
                            action.priority === 'critical' ? 'bg-red-500' :
                            action.priority === 'high' ? 'bg-orange-500' : 'bg-amber-500',
                            'text-white'
                          )}>
                            {action.priority}
                          </Badge>
                          <div className="flex-1">
                            <p className="font-medium">{action.item}</p>
                            <p className="text-sm text-gray-600">{action.reason}</p>
                          </div>
                          <Badge variant="outline">{action.action}</Badge>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Reorder Suggestions */}
              {inventoryRecs.reorder_suggestions?.length > 0 && (
                <Card>
                  <CardHeader>
                    <CardTitle>Reorder Suggestions</CardTitle>
                    <CardDescription>AI-recommended order quantities</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <table className="w-full">
                      <thead>
                        <tr className="text-left border-b">
                          <th className="pb-3 font-medium text-gray-600">Item</th>
                          <th className="pb-3 font-medium text-gray-600 text-right">Current Stock</th>
                          <th className="pb-3 font-medium text-gray-600 text-right">Suggested Order</th>
                          <th className="pb-3 font-medium text-gray-600 text-right">Days Left</th>
                        </tr>
                      </thead>
                      <tbody>
                        {inventoryRecs.reorder_suggestions.map((item, idx) => (
                          <tr key={idx} className="border-b last:border-0">
                            <td className="py-3 font-medium">{item.item}</td>
                            <td className="py-3 text-right">{item.current_stock}</td>
                            <td className="py-3 text-right font-bold text-purple-600">{item.suggested_order}</td>
                            <td className="py-3 text-right">
                              <Badge variant={item.estimated_days_remaining < 3 ? 'destructive' : 'outline'}>
                                {item.estimated_days_remaining} days
                              </Badge>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </CardContent>
                </Card>
              )}

              {/* Optimization Tips */}
              {inventoryRecs.optimization_tips?.length > 0 && (
                <Card className="border-green-200 bg-green-50/50">
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <Lightbulb className="w-5 h-5 text-green-600" />
                      Cost Optimization Tips
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <ul className="space-y-2">
                      {inventoryRecs.optimization_tips.map((tip, idx) => (
                        <li key={idx} className="flex items-start gap-3 p-2">
                          <CheckCircle className="w-5 h-5 text-green-500 mt-0.5" />
                          <span>{tip}</span>
                        </li>
                      ))}
                    </ul>
                  </CardContent>
                </Card>
              )}
            </>
          ) : (
            <Card>
              <CardContent className="py-12 text-center">
                <Package className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                <p className="text-gray-500">Inventory analysis not loaded yet</p>
                <Button className="mt-4" onClick={fetchInventoryRecs}>
                  Load Inventory AI
                </Button>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        {/* Staffing AI Tab */}
        <TabsContent value="staffing" className="space-y-6 mt-6">
          {loading.staffing ? (
            <Card>
              <CardContent className="flex items-center justify-center py-20">
                <div className="text-center">
                  <Loader2 className="w-10 h-10 animate-spin text-purple-600 mx-auto mb-4" />
                  <p className="text-gray-600">Calculating optimal staffing levels...</p>
                </div>
              </CardContent>
            </Card>
          ) : staffingRecs?.success ? (
            <>
              {/* Weekly Summary */}
              {staffingRecs.weekly_summary && (
                <Card>
                  <CardHeader>
                    <CardTitle>Weekly Staffing Overview</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="p-4 bg-purple-50 rounded-lg">
                        <p className="text-sm text-gray-500">Recommended Total Hours</p>
                        <p className="text-3xl font-bold text-purple-700">
                          {staffingRecs.weekly_summary.total_hours_recommended}
                        </p>
                      </div>
                      <div className="space-y-2">
                        <p className="text-sm text-gray-500">Cost Optimization Tips</p>
                        {staffingRecs.weekly_summary.cost_optimization_tips?.map((tip, idx) => (
                          <div key={idx} className="flex items-center gap-2 text-sm">
                            <CheckCircle className="w-4 h-4 text-green-500" />
                            {tip}
                          </div>
                        ))}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Daily Staffing */}
              <Card>
                <CardHeader>
                  <CardTitle>Daily Staffing Recommendations</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    {staffingRecs.daily_staffing?.map((day, idx) => (
                      <div key={idx} className="p-4 rounded-lg border hover:shadow-md transition-shadow">
                        <div className="flex items-center justify-between mb-3">
                          <div>
                            <p className="font-bold text-lg">{day.day}</p>
                            <p className="text-sm text-gray-500">{day.date}</p>
                          </div>
                          <Badge className={cn(
                            day.predicted_demand === 'high' ? 'bg-red-100 text-red-700' :
                            day.predicted_demand === 'medium' ? 'bg-amber-100 text-amber-700' :
                            'bg-green-100 text-green-700'
                          )}>
                            {day.predicted_demand} demand
                          </Badge>
                        </div>
                        
                        <div className="grid grid-cols-4 gap-4 mb-3">
                          <div className="text-center p-2 bg-gray-50 rounded">
                            <p className="text-2xl font-bold">{day.recommended_staff?.kitchen || 0}</p>
                            <p className="text-xs text-gray-500">Kitchen</p>
                          </div>
                          <div className="text-center p-2 bg-gray-50 rounded">
                            <p className="text-2xl font-bold">{day.recommended_staff?.servers || 0}</p>
                            <p className="text-xs text-gray-500">Servers</p>
                          </div>
                          <div className="text-center p-2 bg-gray-50 rounded">
                            <p className="text-2xl font-bold">{day.recommended_staff?.hosts || 0}</p>
                            <p className="text-xs text-gray-500">Hosts</p>
                          </div>
                          <div className="text-center p-2 bg-purple-50 rounded">
                            <p className="text-2xl font-bold text-purple-700">{day.recommended_staff?.total || 0}</p>
                            <p className="text-xs text-gray-500">Total</p>
                          </div>
                        </div>
                        
                        {day.peak_hours?.length > 0 && (
                          <div className="flex items-center gap-2 text-sm text-gray-600">
                            <Clock className="w-4 h-4" />
                            Peak hours: {day.peak_hours.join(', ')}
                          </div>
                        )}
                        
                        {day.notes && (
                          <p className="text-sm text-gray-500 mt-2 italic">{day.notes}</p>
                        )}
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>

              {/* Alerts */}
              {staffingRecs.alerts?.length > 0 && (
                <Card className="border-amber-200">
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2 text-amber-700">
                      <AlertTriangle className="w-5 h-5" />
                      Staffing Alerts
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <ul className="space-y-2">
                      {staffingRecs.alerts.map((alert, idx) => (
                        <li key={idx} className="flex items-center gap-2 text-amber-800">
                          <AlertTriangle className="w-4 h-4" />
                          {alert}
                        </li>
                      ))}
                    </ul>
                  </CardContent>
                </Card>
              )}
            </>
          ) : (
            <Card>
              <CardContent className="py-12 text-center">
                <UserCheck className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                <p className="text-gray-500">Staffing analysis not loaded yet</p>
                <Button className="mt-4" onClick={fetchStaffingRecs}>
                  Load Staffing AI
                </Button>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        {/* Menu AI Tab */}
        <TabsContent value="menu" className="space-y-6 mt-6">
          {loading.menu ? (
            <Card>
              <CardContent className="flex items-center justify-center py-20">
                <div className="text-center">
                  <Loader2 className="w-10 h-10 animate-spin text-purple-600 mx-auto mb-4" />
                  <p className="text-gray-600">Analyzing menu performance...</p>
                </div>
              </CardContent>
            </Card>
          ) : menuRecs?.success ? (
            <>
              {/* Menu Engineering Matrix */}
              {menuRecs.menu_analysis && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <Card className="border-green-200">
                    <CardHeader className="pb-2">
                      <CardTitle className="text-green-700 flex items-center gap-2">
                        <Sparkles className="w-5 h-5" />
                        Stars
                      </CardTitle>
                      <CardDescription>High profit, high popularity</CardDescription>
                    </CardHeader>
                    <CardContent>
                      <div className="flex flex-wrap gap-2">
                        {menuRecs.menu_analysis.stars?.map((item, idx) => (
                          <Badge key={idx} className="bg-green-100 text-green-700">{item}</Badge>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                  
                  <Card className="border-amber-200">
                    <CardHeader className="pb-2">
                      <CardTitle className="text-amber-700 flex items-center gap-2">
                        <Target className="w-5 h-5" />
                        Puzzles
                      </CardTitle>
                      <CardDescription>High profit, needs promotion</CardDescription>
                    </CardHeader>
                    <CardContent>
                      <div className="flex flex-wrap gap-2">
                        {menuRecs.menu_analysis.puzzles?.map((item, idx) => (
                          <Badge key={idx} className="bg-amber-100 text-amber-700">{item}</Badge>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                  
                  <Card className="border-blue-200">
                    <CardHeader className="pb-2">
                      <CardTitle className="text-blue-700 flex items-center gap-2">
                        <TrendingUp className="w-5 h-5" />
                        Plowhorses
                      </CardTitle>
                      <CardDescription>Popular, optimize pricing</CardDescription>
                    </CardHeader>
                    <CardContent>
                      <div className="flex flex-wrap gap-2">
                        {menuRecs.menu_analysis.plowhorses?.map((item, idx) => (
                          <Badge key={idx} className="bg-blue-100 text-blue-700">{item}</Badge>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                  
                  <Card className="border-gray-200">
                    <CardHeader className="pb-2">
                      <CardTitle className="text-gray-700 flex items-center gap-2">
                        <TrendingDown className="w-5 h-5" />
                        Dogs
                      </CardTitle>
                      <CardDescription>Consider removing/redesigning</CardDescription>
                    </CardHeader>
                    <CardContent>
                      <div className="flex flex-wrap gap-2">
                        {menuRecs.menu_analysis.dogs?.map((item, idx) => (
                          <Badge key={idx} variant="outline">{item}</Badge>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                </div>
              )}

              {/* Recommendations */}
              {menuRecs.recommendations?.length > 0 && (
                <Card>
                  <CardHeader>
                    <CardTitle>Menu Recommendations</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-3">
                      {menuRecs.recommendations.map((rec, idx) => (
                        <div key={idx} className="flex items-start gap-4 p-3 rounded-lg border">
                          <Badge className={cn(
                            rec.action === 'promote' ? 'bg-green-100 text-green-700' :
                            rec.action === 'reprice' ? 'bg-blue-100 text-blue-700' :
                            rec.action === 'redesign' ? 'bg-amber-100 text-amber-700' :
                            'bg-red-100 text-red-700'
                          )}>
                            {rec.action}
                          </Badge>
                          <div className="flex-1">
                            <p className="font-medium">{rec.item}</p>
                            <p className="text-sm text-gray-600">{rec.reason}</p>
                            {rec.expected_impact && (
                              <p className="text-sm text-purple-600 mt-1">Impact: {rec.expected_impact}</p>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Upsell Opportunities */}
              {menuRecs.upsell_opportunities?.length > 0 && (
                <Card className="border-purple-200 bg-purple-50/50">
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <DollarSign className="w-5 h-5 text-purple-600" />
                      Upsell Opportunities
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <ul className="space-y-2">
                      {menuRecs.upsell_opportunities.map((opp, idx) => (
                        <li key={idx} className="flex items-start gap-3">
                          <ArrowUpRight className="w-5 h-5 text-purple-500 mt-0.5" />
                          <span>{opp}</span>
                        </li>
                      ))}
                    </ul>
                  </CardContent>
                </Card>
              )}
            </>
          ) : (
            <Card>
              <CardContent className="py-12 text-center">
                <Utensils className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                <p className="text-gray-500">Menu analysis not loaded yet</p>
                <Button className="mt-4" onClick={fetchMenuRecs}>
                  Load Menu AI
                </Button>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        {/* Business Insights Tab */}
        <TabsContent value="insights" className="space-y-6 mt-6">
          {loading.insights ? (
            <Card>
              <CardContent className="flex items-center justify-center py-20">
                <div className="text-center">
                  <Loader2 className="w-10 h-10 animate-spin text-purple-600 mx-auto mb-4" />
                  <p className="text-gray-600">Generating executive insights...</p>
                </div>
              </CardContent>
            </Card>
          ) : businessInsights?.success ? (
            <>
              {/* Executive Summary */}
              {businessInsights.executive_summary && (
                <Card className="border-purple-200 bg-gradient-to-r from-purple-50 to-blue-50">
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <Brain className="w-6 h-6 text-purple-600" />
                      Executive Summary
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-lg text-gray-700">{businessInsights.executive_summary}</p>
                  </CardContent>
                </Card>
              )}

              {/* KPI Highlights */}
              {businessInsights.kpi_highlights?.length > 0 && (
                <Card>
                  <CardHeader>
                    <CardTitle>KPI Highlights</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                      {businessInsights.kpi_highlights.map((kpi, idx) => (
                        <div key={idx} className="p-4 rounded-lg border">
                          <div className="flex items-center justify-between mb-2">
                            <p className="text-sm text-gray-500">{kpi.metric}</p>
                            {getTrendIcon(kpi.trend)}
                          </div>
                          <p className="text-2xl font-bold">{kpi.value}</p>
                          <p className="text-sm text-gray-600 mt-1">{kpi.significance}</p>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Opportunities & Risks */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {businessInsights.opportunities?.length > 0 && (
                  <Card className="border-green-200">
                    <CardHeader>
                      <CardTitle className="text-green-700">Opportunities</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-3">
                        {businessInsights.opportunities.map((opp, idx) => (
                          <div key={idx} className="p-3 rounded-lg bg-green-50">
                            <div className="flex items-center justify-between mb-1">
                              <p className="font-medium">{opp.title}</p>
                              <Badge className={cn(
                                opp.priority === 'high' ? 'bg-red-100 text-red-700' :
                                opp.priority === 'medium' ? 'bg-amber-100 text-amber-700' :
                                'bg-gray-100 text-gray-700'
                              )}>
                                {opp.priority}
                              </Badge>
                            </div>
                            <p className="text-sm text-gray-600">{opp.description}</p>
                            {opp.potential_impact && (
                              <p className="text-sm text-green-600 mt-1">Impact: {opp.potential_impact}</p>
                            )}
                          </div>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                )}

                {businessInsights.risks?.length > 0 && (
                  <Card className="border-red-200">
                    <CardHeader>
                      <CardTitle className="text-red-700">Risks</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-3">
                        {businessInsights.risks.map((risk, idx) => (
                          <div key={idx} className="p-3 rounded-lg bg-red-50">
                            <div className="flex items-center justify-between mb-1">
                              <p className="font-medium">{risk.title}</p>
                              <Badge className={cn(
                                risk.urgency === 'immediate' ? 'bg-red-500 text-white' :
                                risk.urgency === 'soon' ? 'bg-amber-500 text-white' :
                                'bg-gray-100 text-gray-700'
                              )}>
                                {risk.urgency}
                              </Badge>
                            </div>
                            <p className="text-sm text-gray-600">{risk.description}</p>
                            {risk.mitigation && (
                              <p className="text-sm text-blue-600 mt-1">Mitigation: {risk.mitigation}</p>
                            )}
                          </div>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                )}
              </div>

              {/* Action Items */}
              {businessInsights.action_items?.length > 0 && (
                <Card>
                  <CardHeader>
                    <CardTitle>Recommended Actions</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <table className="w-full">
                      <thead>
                        <tr className="text-left border-b">
                          <th className="pb-3 font-medium text-gray-600">Action</th>
                          <th className="pb-3 font-medium text-gray-600">Owner</th>
                          <th className="pb-3 font-medium text-gray-600">Timeline</th>
                          <th className="pb-3 font-medium text-gray-600">Expected Outcome</th>
                        </tr>
                      </thead>
                      <tbody>
                        {businessInsights.action_items.map((item, idx) => (
                          <tr key={idx} className="border-b last:border-0">
                            <td className="py-3 font-medium">{item.action}</td>
                            <td className="py-3 text-gray-600">{item.owner}</td>
                            <td className="py-3">
                              <Badge variant="outline">{item.timeline}</Badge>
                            </td>
                            <td className="py-3 text-sm text-gray-600">{item.expected_outcome}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </CardContent>
                </Card>
              )}
            </>
          ) : (
            <Card>
              <CardContent className="py-12 text-center">
                <Lightbulb className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                <p className="text-gray-500">Business insights not loaded yet</p>
                <Button className="mt-4" onClick={fetchBusinessInsights}>
                  Generate Business Insights
                </Button>
              </CardContent>
            </Card>
          )}
        </TabsContent>
      </Tabs>

      {/* Footer - Last Update Info */}
      <Card className="border-gray-200 bg-gray-50">
        <CardContent className="p-4 flex items-center justify-between">
          <div className="flex items-center gap-2 text-sm text-gray-500">
            <Cpu className="w-4 h-4" />
            AI-powered analytics engine
          </div>
          <p className="text-xs text-gray-400">
            Last analysis: {new Date().toLocaleString()}
          </p>
        </CardContent>
      </Card>
    </div>
  );
}
